/**
 * Typed fetch client to the worker API (§17, §19.1). Read-only increment: only
 * the session + queue read endpoints are wired here (no write scope, no pack
 * station, no carriers). All calls send credentials so the cookie session
 * (§19.1) rides along; the base URL is VITE_API_BASE_URL (the worker the SPA
 * already targets), falling back to "/api" which Vite proxies to :3000 in dev.
 *
 * Wire shapes below mirror the SHARED AUTH/API CONTRACT this increment is built
 * to. Where the worker enriches rows over time (e.g. a customer name on the
 * list), the field is optional so the SPA stays empty-safe.
 */
import type { OrderStatus, ServiceClass, StoreRegion, Role, OrderDTO, Condition, ReadinessEffect, NotificationChannel, FulfilmentMode, BatchStatus, BatchItemStatus } from "@cairn/shared";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** VITE_API_BASE_URL when set (prod / cross-origin), else "/api" (dev proxy). */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "") || "/api";

/** Exposed so Login can build the full SSO redirect URL (a real navigation). */
export const apiBase = BASE;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let detail: unknown = undefined;
    try {
      detail = await res.json();
    } catch {
      // non-JSON error body; ignore.
    }
    throw new ApiError(`${init?.method ?? "GET"} ${path} → ${res.status}`, res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
}

function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined });
}

function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}

/** Build a `?a=1&b=2` suffix from defined params (§B-8 paginated reads). */
function qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ---------------------------------------------------------------------------
// Session / auth (§19.1)
// ---------------------------------------------------------------------------

/** The session user surfaced by GET /me. Email-keyed (Google Workspace). */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  /** Role in the ACTIVE org (§v1.8 Membership.role); kept on `user` for web compat. */
  role: Role;
}

/** §v1.8 — one org the user belongs to (GET /me memberships). */
export interface OrgMembership {
  orgId: string;
  orgName: string;
  role: Role;
}

/** GET /me — never 401s; the SPA polls it to learn auth state. */
export type MeResponse =
  | {
      authenticated: true;
      user: SessionUser;
      activeOrgId: string;
      defaultOrgId: string;
      memberships: OrgMembership[];
    }
  | { authenticated: false };

// ---------------------------------------------------------------------------
// Queue list (GET /orders) — lightweight rows (§17, §18 /queue, §30)
// ---------------------------------------------------------------------------

/** One row in the queue table. Server-sorted express-first (§30) — do NOT re-sort. */
export interface OrderRow {
  id: string;
  name: string; // "#58348"
  storeId: string;
  storeRegion?: StoreRegion | null;
  storeName?: string | null;
  status: OrderStatus;
  fulfilmentType?: string | null;
  exceptionReason?: string | null;
  /** Customer display name (may be absent until the worker enriches the row). */
  customerName?: string | null;
  destinationCountry: string; // ISO-2
  shippingMethodTitle: string;
  /** §A Shopify-parity fields surfaced for the queue (financial/gateway/tags). */
  financialStatus?: string | null;
  paymentGateway?: string | null;
  tags?: string[] | null;
  carrierCode?: string | null;
  serviceCode?: string | null;
  serviceClass?: ServiceClass | null;
  currency: string;
  totalPrice: number;
  /** Theoretical/items weight in grams; kg conversion is display-only (§5). */
  itemsWeightG?: number | null;
  theoreticalWeightG?: number | null;
  lineCount: number;
  createdAtShopify: string; // ISO timestamp
  hasPickupPoint?: boolean | null;
  /** §Task4 customs summary (incoterm + value); null/absent for intra-EU. */
  customs?: OrderCustoms | null;
}

export interface QueuePage {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
  orders: OrderRow[];
}

/** GET /orders/counts — per-status tab counts (§18 status tabs). */
export interface OrderCounts {
  byStatus: Partial<Record<OrderStatus, number>>;
  total: number;
}

/** One distinct facet value + how many orders carry it (§A filter menus). */
export interface FacetValue {
  value: string;
  count: number;
}

/** GET /orders/facets — distinct values for the data-driven queue filters (§A). */
export interface OrderFacets {
  financialStatus: FacetValue[];
  paymentGateway: FacetValue[];
  shippingMethod: FacetValue[];
  carrierCode: FacetValue[];
  destinationCountry: FacetValue[];
  tags: FacetValue[];
}

// ---------------------------------------------------------------------------
// Order detail (GET /orders/:id) — drawer payload (§18 /queue/:orderId)
// ---------------------------------------------------------------------------

/** §Task4 — on-read customs summary for a non-EU order (null for intra-EU). */
export interface OrderCustoms {
  incoterm: string | null;
  customsValueTotal: number | null;
  customsCurrency: string;
  valueBasis: string | null;
  complete: boolean;
}

/** §10.2 importer of record. ENTITY → a distinct importing party (shown in full);
 *  CUSTOMER → the recipient is the importer (party null). null for intra-EU. */
export interface OrderImporterOfRecordParty {
  name: string;
  address: {
    name?: string | null;
    company?: string | null;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    province?: string | null;
    postcode?: string | null;
    country?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  eori: string | null;
  vatNumber: string | null;
  ein: string | null;
  craNumber: string | null;
  importerIds: Record<string, string> | null;
}
export interface OrderImporterOfRecord {
  type: "ENTITY" | "CUSTOMER";
  party: OrderImporterOfRecordParty | null;
}

export interface OrderDetailSummary {
  id: string;
  name: string;
  storeId: string;
  storeRegion?: StoreRegion | null;
  storeName?: string | null;
  status: OrderStatus;
  fulfilmentType?: string | null;
  exceptionReason?: string | null;
  destinationCountry?: string | null;
  shippingMethodTitle?: string | null;
  carrierCode?: string | null;
  serviceCode?: string | null;
  serviceClass?: ServiceClass | null;
  currency: string;
  totalPrice?: number | null;
  itemsWeightG?: number | null;
  theoreticalWeightG?: number | null;
  createdAtShopify?: string | null;
  /** §Task4 customs (incoterm + value); null/absent for intra-EU. */
  customs?: OrderCustoms | null;
  /** §10.2 importer of record; null/absent for intra-EU or no configured lane. */
  importerOfRecord?: OrderImporterOfRecord | null;
}

export interface OrderDetailLine {
  sku: string | null;
  barcode: string | null;
  title: string;
  variantTitle?: string | null;
  quantity: number;
  unitWeightG: number | null;
  /** §Task3 product/variant image (read-only); null when absent. */
  imageUrl?: string | null;
  hsCode: string | null;
  countryOfOrigin: string | null;
  requiresCustoms: boolean;
}

/** Best-effort shipping address parsed from order.raw (§ may be partial/null). */
export interface OrderDetailAddress {
  name?: string | null;
  company?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  province?: string | null;
  zip?: string | null;
  country?: string | null;
  countryCode?: string | null;
  phone?: string | null;
}

/** An order audit/state event; detailJson carries the routing/readiness trace. */
export interface OrderEvent {
  type: string; // ROUTED | READY | READINESS_PENDING | EXCEPTION
  detailJson: unknown;
  createdAt: string;
}

export interface OrderShipment {
  carrierCode: string;
  serviceCode: string;
  status: string;
  trackingNumber: string | null;
}

export interface OrderDetail {
  order: OrderDetailSummary;
  lines: OrderDetailLine[];
  address: OrderDetailAddress | null;
  events: OrderEvent[];
  shipments: OrderShipment[];
}

// ---------------------------------------------------------------------------
// Shipments (GET /shipments) — read-only (§16, §18 /shipments)
// ---------------------------------------------------------------------------

export interface ShipmentRow {
  id: string;
  orderId: string;
  orderName: string;
  storeName?: string | null;
  storeRegion?: StoreRegion | null;
  carrierCode: string;
  serviceCode: string;
  status: string;
  parcelCount: number;
  trackingNumber: string | null;
  trackingUrl: string | null;
  labelAvailable: boolean;
  createdAt: string;
}

export interface ShipmentsPage {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
  shipments: ShipmentRow[];
}

export interface TrackingEventDTO {
  statusCode: string;
  description: string;
  location: string | null;
  occurredAt: string;
}

export interface ShipmentDetail {
  shipment: {
    id: string;
    orderId: string;
    orderName: string;
    storeName?: string | null;
    storeRegion?: StoreRegion | null;
    parcelIndex: number;
    parcelCount: number;
    carrierCode: string;
    serviceCode: string;
    status: string;
    actualWeightG: number | null;
    incoterm: string | null;
    customsValueTotal: number | null;
    customsCurrency: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
    labelAvailable: boolean;
    labelFormat: string | null;
    carrierShipmentId: string | null;
    errorDetail: string | null;
    createdAt: string;
  };
  trackingEvents: TrackingEventDTO[];
}

// ---------------------------------------------------------------------------
// Admin read-only views (GET /admin/*) — §21
// ---------------------------------------------------------------------------

export interface AdminStore {
  id: string;
  name: string;
  shopDomain: string;
  region: StoreRegion;
  currency: string;
  locationId?: string | null;
  apiVersion?: string | null;
  active: boolean;
  /** Per-store override; null = inherit the global Settings default. */
  shortagePolicy: string | null;
  autoCorrectInventory?: boolean;
  packedTag?: string | null;
  /** §B-1 secret status — never the value. */
  accessTokenStatus?: "present" | "missing";
  webhookSecretStatus?: "present" | "missing";
  orderCount: number;
  lastReconcileAt: string | null;
  createdAt: string;
  /** §C optimistic-concurrency token (ISO). */
  updatedAt?: string;
}

/** Non-secret editable store fields (shared by create + update). */
export interface StoreBaseInput {
  name: string;
  shopDomain: string;
  region: StoreRegion;
  defaultCurrency: string;
  locationId?: string | null;
  apiVersion?: string | null;
  active: boolean;
  /** Per-store override; null/omitted = inherit the global Settings default. */
  shortagePolicy?: string | null;
  autoCorrectInventory: boolean;
  packedTag?: string | null;
}
/** Create requires both secrets; update rotates them only when non-blank. */
export interface StoreCreateInput extends StoreBaseInput {
  accessToken: string;
  webhookSecret: string;
}
export interface StoreUpdateInput extends StoreBaseInput {
  accessToken?: string;
  webhookSecret?: string;
}

export interface AdminCarrierService {
  code: string;
  name: string;
  serviceClass: string;
  international: boolean;
}
export interface AdminCarrier {
  code: string;
  displayName: string;
  trackingCompany: string | null;
  services: AdminCarrierService[];
  credentialStatus: "present" | "missing";
  enabled: boolean;
}

/** §B-2 — one CarrierConfigRow, redacted (key names + status, never values). */
export interface AdminCarrierConfig {
  id: string;
  carrierCode: string;
  storeId: string | null;
  enabled: boolean;
  defaultMode: string;
  accountsJson: unknown;
  rateLimitJson: unknown;
  credentialStatus: "present" | "missing";
  credentialKeys: string[];
  // §B-2 test gate — productionReady = enabled AND a passing test-label.
  testConnectionOk: boolean;
  testLabelOk: boolean;
  testConnectionAt: string | null;
  testLabelAt: string | null;
  testDetail: string | null;
  productionReady: boolean;
  updatedAt: string;
}
export type CarrierFieldKind = "secret" | "account" | "option";
export interface CarrierCredentialField {
  key: string;
  label: string;
  required: boolean;
  kind: CarrierFieldKind;
  hint?: string;
}
export interface CarrierConfigInput {
  carrierCode: string;
  storeId?: string | null;
  enabled: boolean;
  defaultMode: string;
  credentials: Record<string, string>;
  accountsJson?: Record<string, unknown> | null;
  rateLimitJson?: Record<string, unknown> | null;
}
export interface CarrierConfigUpdateInput {
  enabled: boolean;
  defaultMode: string;
  credentials?: Record<string, string>;
  accountsJson?: Record<string, unknown> | null;
  rateLimitJson?: Record<string, unknown> | null;
}
export interface CarrierTestResult {
  ok: boolean;
  detail: string;
  services?: string[];
  /** The updated row (test flags refreshed) so callers can re-render without a reload. */
  config?: AdminCarrierConfig;
}
/** GET /admin/carriers payload (registry metadata + per-row configs + field catalog). */
export interface AdminCarriersResponse {
  carriers: AdminCarrier[];
  configs: AdminCarrierConfig[];
  credentialFields: Record<string, CarrierCredentialField[]>;
}

export interface AdminRoutingRule {
  id: string;
  priority: number;
  name: string;
  condition: Condition;
  carrierCode: string;
  serviceCode: string;
  enabled: boolean;
  storeId: string | null;
  /** §C optimistic-concurrency token (ISO); echo back on update. */
  updatedAt?: string;
}

/** Create/update payload for a routing rule (§B-3). */
export interface RoutingRuleInput {
  name: string;
  priority: number;
  condition: Condition;
  carrierCode: string;
  serviceCode: string;
  enabled: boolean;
  storeId?: string | null;
}

/** §8.5 dry-run tester result. */
export interface RoutingTestResult {
  order: { id: string; name: string; storeRegion: string; destinationCountry: string; shippingMethodTitle: string };
  result: {
    matched: boolean;
    ruleId: string | null;
    ruleName: string | null;
    carrierCode: string | null;
    serviceCode: string | null;
    exceptionReason?: string;
    trace: Array<{ ruleId: string; ruleName: string; priority: number; matched: boolean }>;
  };
}

export interface AdminReadinessRule {
  id: string;
  priority: number;
  name: string;
  condition: Condition;
  effect: ReadinessEffect;
  enabled: boolean;
  storeId: string | null;
  /** §C optimistic-concurrency token (ISO); echo back on update. */
  updatedAt?: string;
}

/** Create/update payload for a readiness rule (§B-4). */
export interface ReadinessRuleInput {
  name: string;
  priority: number;
  condition: Condition;
  effect: ReadinessEffect;
  enabled: boolean;
  storeId?: string | null;
}

/** §9 readiness dry-run result. */
export interface ReadinessTestResult {
  order: { id: string; name: string; storeRegion: string; financialStatus: string | null; fraudRisk: string | null };
  result: {
    ready: boolean;
    reasons: Array<{ ruleId: string; ruleName: string; effect: ReadinessEffect; message: string }>;
  };
}

export interface AdminTradeLane {
  id: string;
  name: string;
  destinationCountries: string[];
  storeId?: string | null;
  incoterm: string;
  iorType: string;
  iorEntityId?: string | null;
  valueBasis: string;
  dutiesPayor: string;
  dutiesAccount?: string | null;
  requiredDocs: string[];
  enabled: boolean;
  iorEntityName: string | null;
  updatedAt?: string;
}
/** §B-5 — IOR legal entity. */
export interface AdminLegalEntity {
  id: string;
  name: string;
  addressJson: unknown;
  eori: string | null;
  vatNumber: string | null;
  ein: string | null;
  craNumber: string | null;
  importerIdsJson: unknown;
  updatedAt?: string;
}
export interface TradeLaneInput {
  name: string;
  destinationCountries: string[];
  storeId?: string | null;
  incoterm: string;
  iorType: string;
  iorEntityId?: string | null;
  valueBasis: string;
  dutiesPayor: string;
  dutiesAccount?: string | null;
  requiredDocs: string[];
  enabled: boolean;
}
export interface LegalEntityInput {
  name: string;
  addressJson: Record<string, unknown>;
  eori?: string | null;
  vatNumber?: string | null;
  ein?: string | null;
  craNumber?: string | null;
  importerIdsJson?: Record<string, unknown> | null;
}
export interface LaneOverlap {
  countryCode: string;
  scope: string;
  laneIds: string[];
  laneNames: string[];
}

export interface AdminUser {
  id: string;
  name: string;
  email: string | null;
  role: Role;
  active: boolean;
  /** §B-6 — whether a packer PIN is set (never the hash). */
  hasPin?: boolean;
  createdAt: string;
  updatedAt?: string;
}
/** Create/update payload for a user (§B-6). PIN is set via the separate action. */
export interface UserInput {
  name: string;
  email?: string | null;
  role: Role;
  active: boolean;
}

export interface AdminStation {
  id: string;
  name: string;
  active: boolean;
  printerConfigured: boolean;
  scaleConfigured: boolean;
  /** §B-7 trust lifecycle (§18). */
  printNodeId?: number | null;
  scaleConfig?: Record<string, unknown> | null;
  enrolledAt?: string | null;
  trustExpiresAt?: string | null;
  revoked?: boolean;
  lastSeenAt?: string | null;
  trustStatus?: "trusted" | "expired" | "revoked" | "untrusted";
  updatedAt?: string;
}
/** Create/update payload for a station (§B-7). */
export interface StationInput {
  name: string;
  printNodeId?: number | null;
  scaleConfig?: Record<string, unknown> | null;
  active: boolean;
}

/** §B-9 — global app settings singleton. */
export interface AdminSettings {
  weightTolerancePercent: number;
  weightToleranceFloorG: number;
  photoRetentionDays: number;
  labelRetentionDays: number;
  lockIdleTtlMs: number;
  addressProvider: string;
  /** Global default shortage policy; stores may override. */
  shortagePolicy: string;
  shortageNotifyChannel: NotificationChannel;
  shortageNotifyTarget: string | null;
  slipLanguages: string[];
  stationTrustDays: number;
  updatedAt: string | null;
}
export type SettingsInput = Omit<AdminSettings, "updatedAt">;

/** §B-8 — SKU↔barcode alias + bin location. */
export interface AdminSkuBarcode {
  id: string;
  sku: string;
  barcode: string;
  isPrimary: boolean;
  updatedAt?: string;
}
export interface AdminSkuLocation {
  id: string;
  sku: string;
  primaryLocation: string;
  secondaryLocation: string | null;
  updatedAt?: string;
}
export interface SkuBarcodeInput {
  sku: string;
  barcode: string;
  isPrimary: boolean;
}
export interface SkuLocationInput {
  sku: string;
  primaryLocation: string;
  secondaryLocation?: string | null;
}
export interface ImportSummary {
  total: number;
  upserted: number;
  errors: number;
}
export interface ImportResult {
  sku: string;
  barcode?: string;
  status: "upserted" | "error";
  error?: string;
}

// ---------------------------------------------------------------------------
// Fulfilment documents (§12 — Pack-workflow Tasks 4 & 5)
// ---------------------------------------------------------------------------

/** A postal party on a printed document (ship-to / bill-to / warehouse-from). */
export interface DocAddress {
  name?: string | null;
  company?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  province?: string | null;
  postcode?: string | null;
  country?: string | null; // ISO-2 or display name
  phone?: string | null;
  email?: string | null;
}

/** Branding/contact block in a document footer. */
export interface DocOrg {
  name: string;
  website: string;
  email: string;
  phone: string;
}

export interface PackingSlipLine {
  title: string;
  variantTitle?: string | null;
  sku: string | null;
  barcode?: string | null;
  quantity: number;
  unitWeightG: number | null;
  imageUrl?: string | null;
}

/** GET /orders/:id/packing-slip — everything the A4 packing slip renders (§12.3). */
export interface PackingSlipData {
  order: { id: string; name: string; createdAtShopify: string; currency: string; storeRegion?: StoreRegion | null };
  /** The Code-128 scan key (= Order.id) the bench reads to open this order. */
  barcodeValue: string;
  customerEmail: string | null;
  deliveryMethod: string;
  paymentMethod: string;
  shipTo: DocAddress;
  billTo: DocAddress | null; // null → "same as delivery address"
  warehouse: DocAddress;
  org: DocOrg;
  lines: PackingSlipLine[];
  totalWeightG: number;
  totalItems: number;
}

export type PickingListLayout = "consolidated" | "per-order";

/** One consolidated row: a SKU aggregated across the batch, sorted by bin. */
export interface PickingListConsolidatedRow {
  sku: string | null;
  title: string;
  bin: string | null;
  quantity: number;
  imageUrl?: string | null;
}

/** One per-order row (matches the EasyScan example columns). */
export interface PickingListPerOrderRow {
  sku: string | null;
  title: string;
  bin: string | null;
  imageUrl?: string | null;
  /** Per-order breakdown: which orders need this SKU and how many. */
  perOrder: Array<{ orderName: string; quantity: number }>;
  required: number;
}

/** GET /batches/:id/picking-list?layout= — the A4 picking list (§12.2). */
export interface PickingListData {
  batchId: string;
  layout: PickingListLayout;
  org: DocOrg;
  warehouse: DocAddress;
  generatedAt: string;
  orderCount: number;
  totalItemsRequired: number;
  consolidated: PickingListConsolidatedRow[];
  perOrder: PickingListPerOrderRow[];
}

// ---------------------------------------------------------------------------
// Batches (§12.1, §15, §40 — Pack-workflow Tasks 2 & 3)
// ---------------------------------------------------------------------------

/** Per-order progress within a batch (the §40.5 traceability row). */
export interface BatchItemRow {
  orderId: string;
  orderName: string;
  storeRegion?: StoreRegion | null;
  destinationCountry: string;
  carrierCode: string | null;
  serviceCode: string | null;
  labelStatus: BatchItemStatus;
  /** Pack progress mirrored for the bench (PACKED when the parcel is done). */
  packed: boolean;
  trackingNumber: string | null;
  errorDetail: string | null;
  itemsWeightG: number | null;
}

export interface BatchSummary {
  id: string;
  mode: FulfilmentMode;
  status: BatchStatus;
  note: string | null;
  createdAt: string;
  createdBy: string;
  orderCount: number;
  /** Rollup counts across items (pending/labelled/printed/packed/failed/…). */
  counts: Partial<Record<BatchItemStatus, number>> & { packed: number };
}

export interface BatchDetailData {
  batch: BatchSummary;
  items: BatchItemRow[];
}

export interface CreateBatchInput {
  orderIds: string[];
  mode: FulfilmentMode;
  note?: string | null;
}

// ---------------------------------------------------------------------------
// Client surface
// ---------------------------------------------------------------------------

export const api = {
  /** GET /me — current session (authenticated flag + user). Never throws on 401. */
  getMe(): Promise<MeResponse> {
    return request<MeResponse>(`/me`);
  },

  /** POST /auth/logout — clears the session cookie (204). */
  logout(): Promise<void> {
    return post<void>(`/auth/logout`);
  },

  /** GET /orders — paginated, filtered, express-first (server order). */
  getOrders(params: URLSearchParams): Promise<QueuePage> {
    const qs = params.toString();
    return request<QueuePage>(`/orders${qs ? `?${qs}` : ""}`);
  },

  /** GET /orders/:id — drawer detail (lines, address, events, shipments). */
  getOrder(id: string): Promise<OrderDetail> {
    return request<OrderDetail>(`/orders/${encodeURIComponent(id)}`);
  },

  /** GET /orders/counts — per-status tab counts for the same filter set. */
  getOrderCounts(params: URLSearchParams): Promise<OrderCounts> {
    const qs = params.toString();
    return request<OrderCounts>(`/orders/counts${qs ? `?${qs}` : ""}`);
  },

  /** GET /orders/facets — distinct values for the data-driven filter menus (§A). */
  getOrderFacets(): Promise<OrderFacets> {
    return request<OrderFacets>(`/orders/facets`);
  },

  /** GET /shipments — paginated, filtered (store/carrier/status), recent-first. */
  getShipments(params: URLSearchParams): Promise<ShipmentsPage> {
    const qs = params.toString();
    return request<ShipmentsPage>(`/shipments${qs ? `?${qs}` : ""}`);
  },

  /** GET /shipments/:id — shipment detail + tracking events. */
  getShipment(id: string): Promise<ShipmentDetail> {
    return request<ShipmentDetail>(`/shipments/${encodeURIComponent(id)}`);
  },

  // -------------------------------------------------------------------------
  // Fulfilment documents (§12 — Tasks 4 & 5)
  // -------------------------------------------------------------------------
  /** GET /orders/:id/packing-slip — the A4 packing slip payload (§12.3). */
  getPackingSlip(orderId: string): Promise<PackingSlipData> {
    return request<PackingSlipData>(`/orders/${encodeURIComponent(orderId)}/packing-slip`);
  },
  /** GET /batches/:id/picking-list?layout= — the A4 picking list payload (§12.2). */
  getPickingList(batchId: string, layout: PickingListLayout): Promise<PickingListData> {
    return request<PickingListData>(`/batches/${encodeURIComponent(batchId)}/picking-list${qs({ layout })}`);
  },
  /** GET /batches/:id/packing-slips — every member order's slip, printed together. */
  getBatchPackingSlips(batchId: string): Promise<{ slips: PackingSlipData[] }> {
    return request<{ slips: PackingSlipData[] }>(`/batches/${encodeURIComponent(batchId)}/packing-slips`);
  },

  // -------------------------------------------------------------------------
  // Batches (§12.1, §15, §40 — Tasks 2 & 3)
  // -------------------------------------------------------------------------
  getBatches(): Promise<{ batches: BatchSummary[] }> {
    return request(`/batches`);
  },
  getBatch(id: string): Promise<BatchDetailData> {
    return request(`/batches/${encodeURIComponent(id)}`);
  },
  createBatch(input: CreateBatchInput): Promise<{ batch: BatchSummary }> {
    return post(`/batches`, input);
  },
  /** §40.2 — label-first: generate every order's label in one batch run (mock carrier). */
  printBatchLabels(id: string): Promise<{ batch: BatchSummary }> {
    return post(`/batches/${encodeURIComponent(id)}/print-labels`);
  },
  /** §40.3 — re-enqueue only items not in a successful terminal state. */
  resumeBatch(id: string): Promise<{ batch: BatchSummary }> {
    return post(`/batches/${encodeURIComponent(id)}/resume`);
  },
  retryBatchItem(id: string, orderId: string): Promise<{ batch: BatchSummary }> {
    return post(`/batches/${encodeURIComponent(id)}/items/${encodeURIComponent(orderId)}/retry`);
  },
  /** §40.3 — void an unused (labelled-but-never-packed) item's label. */
  voidBatchItem(id: string, orderId: string): Promise<{ batch: BatchSummary }> {
    return post(`/batches/${encodeURIComponent(id)}/items/${encodeURIComponent(orderId)}/void`);
  },
  skipBatchItem(id: string, orderId: string): Promise<{ batch: BatchSummary }> {
    return post(`/batches/${encodeURIComponent(id)}/items/${encodeURIComponent(orderId)}/skip`);
  },
  /** Pack-completion hook: mark the order's BatchItem PACKED (local-only, §40.4). */
  markPacked(orderId: string): Promise<void> {
    return post<void>(`/pack/${encodeURIComponent(orderId)}/packed`);
  },

  /** Admin config (§21): reads for MANAGER/ADMIN; writes are ADMIN-only (§C). */
  admin: {
    stores: (): Promise<{ stores: AdminStore[] }> => request(`/admin/stores`),
    carriers: (): Promise<AdminCarriersResponse> => request(`/admin/carriers`),
    routingRules: (): Promise<{ rules: AdminRoutingRule[] }> => request(`/admin/routing-rules`),
    readinessRules: (): Promise<{ rules: AdminReadinessRule[] }> => request(`/admin/readiness-rules`),
    tradeLanes: (): Promise<{ lanes: AdminTradeLane[] }> => request(`/admin/trade-lanes`),
    users: (): Promise<{ users: AdminUser[] }> => request(`/admin/users`),
    stations: (): Promise<{ stations: AdminStation[] }> => request(`/admin/stations`),

    // §B-3 routing-rule writes (ADMIN). 409 → ApiError(status=409) on a stale edit.
    createRoutingRule: (input: RoutingRuleInput): Promise<{ rule: AdminRoutingRule }> =>
      post(`/admin/routing-rules`, input),
    updateRoutingRule: (id: string, input: RoutingRuleInput, expectedUpdatedAt?: string): Promise<{ rule: AdminRoutingRule }> =>
      put(`/admin/routing-rules/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteRoutingRule: (id: string): Promise<void> => del(`/admin/routing-rules/${encodeURIComponent(id)}`),
    reorderRoutingRules: (order: string[]): Promise<{ rules: AdminRoutingRule[] }> =>
      post(`/admin/routing-rules/reorder`, { order }),
    testRouting: (orderId: string, rules?: AdminRoutingRule[]): Promise<RoutingTestResult> =>
      post(`/admin/routing-rules/test`, { orderId, rules }),

    // §B-4 readiness-rule writes (ADMIN). 409 → ApiError(status=409) on a stale edit.
    createReadinessRule: (input: ReadinessRuleInput): Promise<{ rule: AdminReadinessRule }> =>
      post(`/admin/readiness-rules`, input),
    updateReadinessRule: (id: string, input: ReadinessRuleInput, expectedUpdatedAt?: string): Promise<{ rule: AdminReadinessRule }> =>
      put(`/admin/readiness-rules/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteReadinessRule: (id: string): Promise<void> => del(`/admin/readiness-rules/${encodeURIComponent(id)}`),
    reorderReadinessRules: (order: string[]): Promise<{ rules: AdminReadinessRule[] }> =>
      post(`/admin/readiness-rules/reorder`, { order }),
    testReadiness: (orderId: string, rules?: AdminReadinessRule[]): Promise<ReadinessTestResult> =>
      post(`/admin/readiness-rules/test`, { orderId, rules }),

    // §B-1 store writes (ADMIN). Secrets are write-only; reads expose status only.
    createStore: (input: StoreCreateInput): Promise<{ store: AdminStore }> => post(`/admin/stores`, input),
    updateStore: (id: string, input: StoreUpdateInput, expectedUpdatedAt?: string): Promise<{ store: AdminStore }> =>
      put(`/admin/stores/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteStore: (id: string): Promise<void> => del(`/admin/stores/${encodeURIComponent(id)}`),
    testStoreConnection: (id: string): Promise<{ ok: boolean; detail: string }> =>
      post(`/admin/stores/${encodeURIComponent(id)}/test-connection`),
    reingestStore: (id: string, mode: "incremental" | "sweep" = "sweep"): Promise<{ enqueued: boolean; mode: string }> =>
      post(`/admin/stores/${encodeURIComponent(id)}/re-ingest`, { mode }),

    // §B-2 carrier-config writes (ADMIN). Credentials are write-only (encrypted blob).
    createCarrierConfig: (input: CarrierConfigInput): Promise<{ config: AdminCarrierConfig }> => post(`/admin/carriers`, input),
    updateCarrierConfig: (id: string, input: CarrierConfigUpdateInput, expectedUpdatedAt?: string): Promise<{ config: AdminCarrierConfig }> =>
      put(`/admin/carriers/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteCarrierConfig: (id: string): Promise<void> => del(`/admin/carriers/${encodeURIComponent(id)}`),
    // Clear credentials + reset the test gate but keep the config row (scope, mode, accounts).
    disconnectCarrier: (id: string, expectedUpdatedAt?: string): Promise<{ config: AdminCarrierConfig }> =>
      post(`/admin/carriers/${encodeURIComponent(id)}/disconnect`, { expectedUpdatedAt }),
    testCarrierConnection: (id: string): Promise<CarrierTestResult> =>
      post(`/admin/carriers/${encodeURIComponent(id)}/test-connection`, {}),
    testCarrierLabel: (id: string, opts?: { serviceCode?: string; destinationCountry?: string }): Promise<CarrierTestResult> =>
      post(`/admin/carriers/${encodeURIComponent(id)}/test-label`, opts ?? {}),

    // §B-6 user / allowlist writes (ADMIN). PIN is one-way (set via action), never returned.
    createUser: (input: UserInput): Promise<{ user: AdminUser }> => post(`/admin/users`, input),
    updateUser: (id: string, input: UserInput, expectedUpdatedAt?: string): Promise<{ user: AdminUser }> =>
      put(`/admin/users/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteUser: (id: string): Promise<void> => del(`/admin/users/${encodeURIComponent(id)}`),
    setUserPin: (id: string, pin: string): Promise<{ user: AdminUser }> => post(`/admin/users/${encodeURIComponent(id)}/set-pin`, { pin }),
    resetUserPin: (id: string): Promise<{ user: AdminUser }> => post(`/admin/users/${encodeURIComponent(id)}/reset-pin`),

    // §B-5 trade-lane + legal-entity writes (ADMIN).
    legalEntities: (): Promise<{ entities: AdminLegalEntity[] }> => request(`/admin/legal-entities`),
    createTradeLane: (input: TradeLaneInput): Promise<{ lane: AdminTradeLane }> => post(`/admin/trade-lanes`, input),
    updateTradeLane: (id: string, input: TradeLaneInput, expectedUpdatedAt?: string): Promise<{ lane: AdminTradeLane }> =>
      put(`/admin/trade-lanes/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteTradeLane: (id: string): Promise<void> => del(`/admin/trade-lanes/${encodeURIComponent(id)}`),
    checkLaneOverlap: (): Promise<{ overlaps: LaneOverlap[] }> => post(`/admin/trade-lanes/overlap-check`),
    createLegalEntity: (input: LegalEntityInput): Promise<{ entity: AdminLegalEntity }> => post(`/admin/legal-entities`, input),
    updateLegalEntity: (id: string, input: LegalEntityInput, expectedUpdatedAt?: string): Promise<{ entity: AdminLegalEntity }> =>
      put(`/admin/legal-entities/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteLegalEntity: (id: string): Promise<void> => del(`/admin/legal-entities/${encodeURIComponent(id)}`),

    // §B-7 station writes (ADMIN) — incl. the §18 enroll/revoke trust lifecycle.
    createStation: (input: StationInput): Promise<{ station: AdminStation }> => post(`/admin/stations`, input),
    updateStation: (id: string, input: StationInput, expectedUpdatedAt?: string): Promise<{ station: AdminStation }> =>
      put(`/admin/stations/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteStation: (id: string): Promise<void> => del(`/admin/stations/${encodeURIComponent(id)}`),
    enrollStation: (id: string, expectedUpdatedAt?: string): Promise<{ station: AdminStation }> =>
      post(`/admin/stations/${encodeURIComponent(id)}/enroll`, { expectedUpdatedAt }),
    revokeStation: (id: string, reason?: string, expectedUpdatedAt?: string): Promise<{ station: AdminStation }> =>
      post(`/admin/stations/${encodeURIComponent(id)}/revoke`, { reason, expectedUpdatedAt }),

    // §B-8 barcode aliases + bin locations (ADMIN writes; reads MANAGER+ADMIN).
    skuBarcodes: (params: { q?: string; primaryOnly?: boolean; page?: number; limit?: number } = {}): Promise<{ aliases: AdminSkuBarcode[]; total: number; page: number; limit: number }> =>
      request(`/admin/sku-barcodes${qs(params)}`),
    createSkuBarcode: (input: SkuBarcodeInput): Promise<{ alias: AdminSkuBarcode }> => post(`/admin/sku-barcodes`, input),
    updateSkuBarcode: (id: string, input: SkuBarcodeInput, expectedUpdatedAt?: string): Promise<{ alias: AdminSkuBarcode }> =>
      put(`/admin/sku-barcodes/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteSkuBarcode: (id: string): Promise<void> => del(`/admin/sku-barcodes/${encodeURIComponent(id)}`),
    importSkuBarcodes: (rows: SkuBarcodeInput[]): Promise<{ results: ImportResult[]; summary: ImportSummary }> => post(`/admin/sku-barcodes/import`, { rows }),
    skuLocations: (params: { q?: string; page?: number; limit?: number } = {}): Promise<{ locations: AdminSkuLocation[]; total: number; page: number; limit: number }> =>
      request(`/admin/sku-locations${qs(params)}`),
    createSkuLocation: (input: SkuLocationInput): Promise<{ location: AdminSkuLocation }> => post(`/admin/sku-locations`, input),
    updateSkuLocation: (id: string, input: SkuLocationInput, expectedUpdatedAt?: string): Promise<{ location: AdminSkuLocation }> =>
      put(`/admin/sku-locations/${encodeURIComponent(id)}`, { ...input, expectedUpdatedAt }),
    deleteSkuLocation: (id: string): Promise<void> => del(`/admin/sku-locations/${encodeURIComponent(id)}`),
    importSkuLocations: (rows: SkuLocationInput[]): Promise<{ results: ImportResult[]; summary: ImportSummary }> => post(`/admin/sku-locations/import`, { rows }),

    // §B-9 app settings (singleton).
    settings: (): Promise<{ settings: AdminSettings }> => request(`/admin/settings`),
    updateSettings: (input: SettingsInput, expectedUpdatedAt?: string | null): Promise<{ settings: AdminSettings }> =>
      put(`/admin/settings`, { ...input, expectedUpdatedAt: expectedUpdatedAt ?? undefined }),
  },

  // -------------------------------------------------------------------------
  // Pack station (§17 /pack/*) — UNUSED in this read-only increment, retained
  // so the existing pack-station scaffold keeps compiling. These endpoints are
  // guarded by INTERNAL_SERVICE_TOKEN on the worker and are NOT exercised by
  // the manager/admin queue UI built here. Do not wire them into new screens.
  // -------------------------------------------------------------------------
  openPack(orderId: string, stationId: string): Promise<PackOpenResponse> {
    return post<PackOpenResponse>(`/pack/${orderId}/open`, { stationId });
  },
  readScale(stationId: string): Promise<{ grams: number; stable: boolean }> {
    return post<{ grams: number; stable: boolean }>(`/stations/${stationId}/scale/read`);
  },
  uploadPhoto(orderId: string, blob: Blob): Promise<void> {
    const fd = new FormData();
    fd.append("photo", blob, `${orderId}.jpg`);
    return request<void>(`/pack/${orderId}/photo`, { method: "POST", body: fd, headers: {} });
  },
  createLabel(orderId: string): Promise<{ trackingNumber: string; trackingUrl: string | null }> {
    return post<{ trackingNumber: string; trackingUrl: string | null }>(`/pack/${orderId}/label`);
  },
  fulfil(orderId: string): Promise<void> {
    return post<void>(`/pack/${orderId}/fulfil`);
  },
  park(orderId: string, reason: string): Promise<void> {
    return post<void>(`/pack/${orderId}/park`, { reason });
  },
};

/** Pack-station open response (legacy scaffold; see note above). */
export interface PackOpenResponse {
  order: OrderDTO;
  /** §29.2 barcode aliases for the order's SKUs. */
  aliases: Array<{ sku: string; barcode: string }>;
  stationId: string;
  /** §15 — fulfilment mode resolved for this order/batch. */
  mode: FulfilmentMode;
  /** §33.1 — pre-printed label tracking to confirm at the bench (LABEL_FIRST). */
  expectedTracking?: string | null;
  /** §Task3 — sku → product image URL for visual item confirmation. */
  lineImages?: Record<string, string>;
}
